// Deploy verdict (#2682) — the value the conversation rollup
// (`packages/db/src/queries/conversation-rollup.ts`) surfaces for a merged PR's
// sha. Three states, closed, deliberate:
//
//   `recorded`               — a `deployment_status` webhook delivery said this
//                              sha deployed. The PRIMARY signal GitHub offers;
//                              live since #3256 (the App holds
//                              `deployments: read` and is subscribed).
//   `workflow_run_fallback`  — RESERVED; no producer writes it (#3256). It was
//                              meant as a `workflow_run.completed` proxy for
//                              "this shipped" — a customer-recorded CI signal,
//                              weaker than a real deploy, and a UI that
//                              conflates the two ships a CI-readiness signal as
//                              a deploy fact. The consumer branch was never fed
//                              and is deleted; the member stays because the
//                              stored shape is a one-way door.
//   `unknown`                — no verdict row exists for this sha in the rollup's
//                              environment, or the PR carries no merge commit to
//                              join on (#3702 wired the join on that column).
//
// The THREE values are a UNION, not a hierarchy: a recordable row carries one
// `source` (today only `recorded` is ever written), and silence is `unknown`.
// The rollup's join, once wired, is keyed on `pr_head_sha` and reads ONE row
// per plan group (newest-first); `recorded` outranks the reserved value should
// a producer for it ever exist, because the deployed event is the more
// specific proof.
//
// Pure: imports only the domain's own types. No I/O.

import { z } from 'zod';

export const DeployVerdictSource = {
  Recorded: 'recorded',
  // Reserved; no producer writes it (#3256). Kept: the stored shape is a one-way door.
  WorkflowRunFallback: 'workflow_run_fallback',
} as const;
export const DeployVerdictSourceSchema = z.enum(DeployVerdictSource);
export type DeployVerdictSource = z.infer<typeof DeployVerdictSourceSchema>;

export const DeployVerdictKind = {
  Recorded: 'recorded',
  WorkflowRunFallback: 'workflow_run_fallback',
  Unknown: 'unknown',
} as const;
export const DeployVerdictKindSchema = z.enum(DeployVerdictKind);
export type DeployVerdictKind = z.infer<typeof DeployVerdictKindSchema>;

/**
 * A merge must be at least this old before its missing verdict counts as missing
 * rather than not-yet-arrived. ONE definition, read by the hourly gap alert
 * (`apps/worker/src/cron/deploy-verdict-gap.ts`) and by `task outcome`'s deploy
 * state (`packages/db/src/queries/task-outcome.ts`), so the two never disagree
 * about when a deploy is late.
 *
 * The chain is asynchronous: CI, then the release, then the deploy, then
 * verify-live's own poll budget. A merge still moving through it has no verdict
 * YET, which is a different fact from never getting one. Measured end-to-end on
 * prod at ~7 min p50 and ~35 min p90 (epic #3254's close report); 90 minutes
 * clears the p90 with room and still finds the gap inside the same working day.
 */
export const DEPLOY_VERDICT_GRACE_MINUTES = 90;

/**
 * What `createDeployVerdict` answers — a DISCRIMINATED verdict, never a bare row,
 * for the reason `createBlocker` and `createRawTask` give: every producer is
 * at-least-once, so "a verdict with this delivery exists" is true after both a
 * first record and a redelivery, and only the first is worth a notification /
 * audit. A bare row collapses the two.
 */
export type RecordDeployVerdictResult =
  | { kind: 'created'; accountId: string; deliveryId: string; source: DeployVerdictSource }
  | { kind: 'existing'; accountId: string; deliveryId: string; source: DeployVerdictSource };
